import React, { useCallback, useEffect, useState } from "react";
import { Landmark, FileText, CheckCircle2, XCircle, Loader2, RefreshCw, Send, Truck } from "lucide-react";

const CHANNELS = ["efax", "email", "mail"];

// Admin view of packages submitted to lenders/servicers, with approve/decline,
// bulk submit, and mark-sent for mailed/emailed packets.
export default function LenderSubmissionsPanel({ client }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkChannel, setBulkChannel] = useState("efax");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/lender-submissions");
      setRows(r.data.submissions || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [client]);
  useEffect(() => { load(); }, [load]);

  const openPackage = (url) => { if (url) window.open(url, "_blank"); };

  const decide = async (app_id, decision) => {
    setBusyId(app_id);
    try {
      await client.post(`/admin/lender-submissions/${app_id}/decision`, { decision });
      await load();
    } catch { /* ignore */ }
    setBusyId(null);
  };

  const markSent = async (app_id) => {
    setBusyId(app_id);
    try {
      const reference = window.prompt("Tracking / reference number (optional):", "") || "";
      await client.post(`/admin/lender-submissions/${app_id}/mark-sent`, { reference });
      await load();
    } catch { /* ignore */ }
    setBusyId(null);
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const bulkSubmit = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true); setBulkMsg(null);
    try {
      const r = await client.post("/admin/lender-submissions/bulk", { app_ids: selectedIds, channel: bulkChannel, dry_run: false });
      setBulkMsg({ ok: true, t: `Submitted ${r.data.submitted}/${r.data.total} via ${bulkChannel}.` });
      setSelected({});
      await load();
    } catch (e) {
      setBulkMsg({ ok: false, t: e?.response?.data?.detail || "Bulk submit failed." });
    }
    setBulkBusy(false);
  };

  return (
    <section id="sec-lender-submissions" className="mb-12 scroll-mt-32" data-testid="admin-lender-submissions">
      <div className="mb-5 flex items-center gap-3">
        <Landmark size={18} className="text-[#B8860B]" />
        <h2 className="text-2xl font-serif">Lender Submissions</h2>
        {rows.length > 0 && (
          <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#B8860B]/15 text-[#B8860B] border border-[#B8860B]/40 rounded-sm">{rows.length} pending</span>
        )}
        <button onClick={load} data-testid="lender-sub-refresh" className="ml-auto text-[#8A8A8A] hover:text-[#ECECE4]"><RefreshCw size={14} /></button>
      </div>

      {rows.length > 0 && (
        <div className="mb-4 flex items-center gap-3 flex-wrap bg-[#13130E] border border-[#2A2A22] rounded-lg p-3.5" data-testid="lender-sub-bulk-bar">
          <span className="text-[12px] text-[#9A9A90]">{selectedIds.length} selected</span>
          <select value={bulkChannel} onChange={(e) => setBulkChannel(e.target.value)} data-testid="lender-sub-bulk-channel"
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-2.5 py-1.5 text-[12px] text-[#ECECE4]">
            {CHANNELS.map((c) => <option key={c} value={c}>{c === "efax" ? "eFax" : c === "email" ? "Email" : "USPS Mail"}</option>)}
          </select>
          <button onClick={bulkSubmit} disabled={bulkBusy || selectedIds.length === 0} data-testid="lender-sub-bulk-submit"
            className="inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-3.5 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm disabled:opacity-40">
            {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Bulk submit ({selectedIds.length})
          </button>
          {bulkMsg && <span className={`text-[11.5px] ${bulkMsg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{bulkMsg.ok ? "✓ " : "⚠ "}{bulkMsg.t}</span>}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[#5C5C5C] text-[13px] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="lender-sub-empty">
          No approval packages awaiting a lender response. Borrower/broker submissions appear here.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((s) => {
            const chan = s.channel === "email" ? "Email" : s.channel === "mail" ? "USPS Mail" : "eFax";
            const dest = s.channel === "email" ? s.lender_email : s.channel === "mail" ? s.lender_address : s.lender_fax;
            return (
            <div key={s.app_id} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5 flex items-start justify-between flex-wrap gap-4" data-testid={`lender-sub-${s.app_id}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={!!selected[s.app_id]} onChange={() => toggle(s.app_id)} data-testid={`lender-sub-select-${s.app_id}`}
                  className="mt-1 accent-[#B8860B] w-4 h-4 shrink-0" />
                <div>
                  <div className="font-semibold text-[14px] text-[#ECECE4]">{s.borrower_name} · {s.agreement}</div>
                  <div className="text-[12px] text-[#9A9A90] mt-1">
                    Lender: <span className="text-[#E4C97A]">{s.current_lender}</span> · Acct {s.account_number} · Balance ${Number(s.current_balance || 0).toLocaleString()}
                  </div>
                  <div className="text-[11px] text-[#8A8A8A] mt-1">
                    {chan}{dest ? ` → ${dest}` : ""} · via {s.submitted_by} · {s.submitted_at ? String(s.submitted_at).slice(0, 10) : ""}
                    {s.broker_name ? ` · ${s.broker_name}` : ""}
                    {" · "}<span className={s.sent ? "text-[#3DBB8E]" : "text-[#E4C97A]"} data-testid={`lender-sub-delivery-${s.app_id}`}>{s.sent ? "sent" : (s.delivery_status || "pending")}</span>
                  </div>
                  {s.needs_print_mail && !s.sent && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-sm border border-[#E4B84A]/50 bg-[#E4B84A]/10 px-2 py-1 text-[10.5px] font-semibold text-[#E4B84A]" data-testid={`lender-sub-needs-mail-${s.app_id}`}>
                      ⚠ No fax/email found — PRINT &amp; MAIL to lender, then mark sent
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {s.package_url && (
                  <button onClick={() => openPackage(s.package_url)} data-testid={`lender-sub-view-${s.app_id}`}
                    className="inline-flex items-center gap-1.5 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#B8860B] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm"><FileText size={12} /> Package</button>
                )}
                {!s.sent && (
                  <button onClick={() => markSent(s.app_id)} disabled={busyId === s.app_id} data-testid={`lender-sub-mark-sent-${s.app_id}`}
                    className="inline-flex items-center gap-1.5 border border-[#2A2A22] hover:border-[#3A3A3A] text-[#9A9A90] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm disabled:opacity-40">
                    {busyId === s.app_id ? <Loader2 size={12} className="animate-spin" /> : <Truck size={12} />} Mark sent
                  </button>
                )}
                <button onClick={() => decide(s.app_id, "approved")} disabled={busyId === s.app_id} data-testid={`lender-sub-approve-${s.app_id}`}
                  className="inline-flex items-center gap-1.5 bg-[#0B6B4F]/20 border border-[#0B6B4F]/70 text-[#3DBB8E] hover:bg-[#0B6B4F]/35 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm disabled:opacity-40">
                  {busyId === s.app_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approved
                </button>
                <button onClick={() => decide(s.app_id, "declined")} disabled={busyId === s.app_id} data-testid={`lender-sub-decline-${s.app_id}`}
                  className="inline-flex items-center gap-1.5 bg-[#9E2A2B]/15 border border-[#9E2A2B]/60 text-[#FF8585] hover:bg-[#9E2A2B]/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm disabled:opacity-40">
                  <XCircle size={12} /> Declined
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
